import type { Dispatch, SetStateAction } from "react"

import { format } from "date-fns"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import type { Location } from "@/types/collections"

import { LocationPicker } from "./LocationPicker"
import type { NewStopFormData, PendingStopData } from "./types"

export function NewStopDialog({
  open,
  onOpenChange,
  pending,
  form,
  setForm,
  locations,
  locationSearchTerm,
  onLocationSearchChange,
  onCreate,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  pending: PendingStopData | null
  form: NewStopFormData
  setForm: Dispatch<SetStateAction<NewStopFormData>>
  locations: Location[]
  locationSearchTerm: string
  onLocationSearchChange: (term: string) => void
  onCreate: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Stop</DialogTitle>
          <DialogDescription>
            {pending && (
              <span>
                Scheduled from {format(pending.startTime, "HH:mm")} to{" "}
                {format(pending.endTime, "HH:mm")}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="startTime">Start Time *</Label>
              <Input
                id="startTime"
                type="time"
                value={form.startTime}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, startTime: e.target.value }))
                }
                className="bg-white"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="endTime">End Time *</Label>
              <Input
                id="endTime"
                type="time"
                value={form.endTime}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, endTime: e.target.value }))
                }
                className="bg-white"
              />
            </div>
          </div>

          <LocationPicker
            locations={locations}
            searchTerm={locationSearchTerm}
            onSearchChange={onLocationSearchChange}
            selectedLocationId={form.locationId}
            onSelect={(locationId) => setForm((prev) => ({ ...prev, locationId }))}
          />

          <div className="grid gap-2">
            <Label htmlFor="slotType">Slot Type *</Label>
            <Select
              value={form.slotType}
              onValueChange={(
                value:
                  | "Shipper Collection"
                  | "Internal Stop"
                  | "Haulage Collection"
                  | "Haulage Delivery"
                  | "Depot Injection"
                  | "Direct Injection"
                  | "Charging Stop",
              ) => setForm((prev) => ({ ...prev, slotType: value }))}
            >
              <SelectTrigger className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="Shipper Collection">Shipper Collection</SelectItem>
                <SelectItem value="Internal Stop">Internal Stop</SelectItem>
                <SelectItem value="Haulage Collection">Haulage Collection</SelectItem>
                <SelectItem value="Haulage Delivery">Haulage Delivery</SelectItem>
                <SelectItem value="Depot Injection">Depot Injection</SelectItem>
                <SelectItem value="Direct Injection">Direct Injection</SelectItem>
                <SelectItem value="Charging Stop">Charging Stop</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.slotType === "Haulage Delivery" && (
            <div className="grid gap-2">
              <Label htmlFor="haulageRevenue">Revenue (£)</Label>
              <Input
                id="haulageRevenue"
                type="number"
                min={0}
                step={0.01}
                placeholder="e.g. 150.00"
                value={form.haulageDeliveryRevenueGbp ?? ""}
                onChange={(e) => {
                  const v = e.target.value
                  setForm((prev) => ({
                    ...prev,
                    haulageDeliveryRevenueGbp:
                      v === "" ? undefined : Math.max(0, parseFloat(v) || 0),
                  }))
                }}
                className="bg-white"
              />
              <p className="text-xs text-palette-jungleGreen500">
                Optional. Shown in route cost breakdown.
              </p>
            </div>
          )}

          {form.slotType === "Direct Injection" && (
            <div className="grid gap-2">
              <Label htmlFor="directInjectionCost">Direct injection cost (£)</Label>
              <Input
                id="directInjectionCost"
                type="number"
                min={0}
                step={0.01}
                placeholder="e.g. 185.00"
                value={form.directInjectionCostGbp ?? ""}
                onChange={(e) => {
                  const v = e.target.value
                  setForm((prev) => ({
                    ...prev,
                    directInjectionCostGbp:
                      v === "" ? undefined : Math.max(0, parseFloat(v) || 0),
                  }))
                }}
                className="bg-white"
              />
              <p className="text-xs text-palette-jungleGreen500">
                The agreed price for this stop. A route with a direct injection on it is
                costed at this price instead of from lease, charging and wages.
              </p>
            </div>
          )}

          {/* Direct injection needs a service: it's how the forecast sync matches the shipper. */}
          {form.slotType !== "Internal Stop" &&
            form.slotType !== "Depot Injection" &&
            form.slotType !== "Charging Stop" && (
              <div className="grid gap-2">
                <Label htmlFor="slotService">Slot Service *</Label>
                <div className="flex flex-wrap gap-2">
                  {["SD", "ND", "2D"].map((service) => (
                    <label
                      key={service}
                      className={cn(
                        "px-3 py-2 text-sm rounded border cursor-pointer transition-colors",
                        form.slotService.includes(service)
                          ? "bg-amber-100 border-amber-300 text-amber-700"
                          : "bg-palette-jungleGreen000 border-palette-jungleGreen200 text-palette-jungleGreen600 hover:bg-palette-jungleGreen100",
                      )}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={form.slotService.includes(service)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setForm((prev) => ({
                              ...prev,
                              slotService: [...prev.slotService, service],
                            }))
                          } else {
                            setForm((prev) => ({
                              ...prev,
                              slotService: prev.slotService.filter((s) => s !== service),
                            }))
                          }
                        }}
                      />
                      {service}
                    </label>
                  ))}
                </div>
              </div>
            )}

          {form.slotType !== "Internal Stop" &&
            form.slotType !== "Depot Injection" &&
            form.slotType !== "Charging Stop" && (
              <div className="grid gap-2">
                <Label htmlFor="slotPnl">Slot P&L *</Label>
                <div className="flex flex-wrap gap-2">
                  {["RC1", "RC2", "HIVED One", "HIVED Haulage"].map((pnl) => (
                    <label
                      key={pnl}
                      className={cn(
                        "px-3 py-2 text-sm rounded border cursor-pointer transition-colors",
                        form.slotPnl.includes(pnl)
                          ? "bg-amber-100 border-amber-300 text-amber-700"
                          : "bg-palette-jungleGreen000 border-palette-jungleGreen200 text-palette-jungleGreen600 hover:bg-palette-jungleGreen100",
                      )}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={form.slotPnl.includes(pnl)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setForm((prev) => ({
                              ...prev,
                              slotPnl: [...prev.slotPnl, pnl],
                            }))
                          } else {
                            setForm((prev) => ({
                              ...prev,
                              slotPnl: prev.slotPnl.filter((p) => p !== pnl),
                            }))
                          }
                        }}
                      />
                      {pnl}
                    </label>
                  ))}
                </div>
              </div>
            )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onCreate}
            disabled={
              !form.locationId ||
              !form.startTime ||
              !form.endTime ||
              (form.slotType !== "Internal Stop" &&
                form.slotType !== "Depot Injection" &&
                form.slotType !== "Charging Stop" &&
                (form.slotService.length === 0 ||
                  form.slotPnl.length === 0))
            }
            variant="confirm"
          >
            Create Stop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
